/**
 * 1.66 (192) — a café's tables and their open bills: the pure rules the till shares with the database.
 * The payment trigger (`private.table_bill_pay`) is the authority; `coversBill` mirrors it so the till can tell
 * before it sends. Prices are never kept on a line: the cart prices a bill like any cart (`priceCart`).
 */
import { tablePhotoSrc } from './table-photo'
import type { CartItem } from '@/components/pos/cart-panel'
import type { Product, ProductUnit } from './mock-data'
import type { Promotion } from './promotions'
import { cartTotalOf, priceCart } from './cart-pricing'
import { toTetri } from './money-round'
import { serviceTetri } from './service-charge'
import { isLateRequest, type GuestRequest, type RequestKind } from './table-requests'

export type Station = 'kitchen' | 'bar'
/** 1.68 (196) — a device's duties in one heartbeat: a station screen, the till's bar, the till that prints checks */
export type PingStation = Station | 'check'
/** 1.68 (196) — the table's furniture shape; empty = chosen from the seats (up to four round, more long) */
export type TableShape = 'round' | 'square' | 'long'

export type Hall = { id: string; branch_id: string; name: string; sort: number; active: boolean }

export type DiningTable = {
  id: string; branch_id: string; hall_id: string | null; name: string; seats: number | null; sort: number; active: boolean
  /** 1.68 (196) */
  shape: TableShape | null
  /** 1.69 (197): a reservation mark — set and cleared only through `table_reserve`, never by a layout save */
  reserved_at: string | null
  reserved_name: string | null
}

export type BillLine = {
  id: string
  order_id: string
  product_id: string | null
  product_name: string
  quantity: number
  note: string | null
  station: Station | null
  status: 'new' | 'sent' | 'void'
  batch: number | null
  added_by_name: string | null
  created_at: string
  /** 1.67 (193): the kitchen's timestamps — never new status values (an old till reads `status`) */
  sent_at: string | null
  ready_at: string | null
  served_at: string | null
}

export type Bill = {
  id: string
  branch_id: string
  table_id: string
  table_name: string
  hall_name: string | null
  status: 'open' | 'paid' | 'cancelled'
  revision: number
  guests: number | null
  opened_at: string
  opened_by_name: string | null
  sent_batches: number
  bill_printed_at: string | null
  bill_total: number | null
  sale_id: string | null
  lines: BillLine[]
  /** 1.67 (193): ORDERING — rises on every write; `revision` stays the money's (the payment check) */
  seq: number
  waiter_id: string | null
  waiter_name: string | null
  check_requested_at: string | null
  /** 1.68 (196): the service rate this bill opened with, basis points (10 % = 1000) — stamped by the database */
  service_bp: number
  /** 1.68 (196): the guests left (the table freed); a paid bill without it keeps its table taken (G22) */
  left_at: string | null
  /** 1.68 (196): the bill's newest printing of its check */
  last_print: LastPrint | null
}

/** 1.68 (196) — one printing of a check (`table_check_prints`, without its snapshot) */
export type LastPrint = {
  id: string; state: 'claimed' | 'sent' | 'failed'; requestAt: string; claimedAt: string; stateAt: string | null; code: string; total: number
}

export type SlipStation = { station: Station; lines: { qty: number; name: string; note: string | null }[] }

export type Batch = {
  id: string
  order_id: string
  kind: 'send' | 'cancel'
  n: number
  slips: SlipStation[]
  reason: string | null
  table_name: string
  hall_name: string | null
  created_by_name: string | null
  created_at: string
}

const num = (v: unknown) => (v == null ? 0 : Number(v))
const numOrNull = (v: unknown) => (v == null ? null : Number(v))
const str = (v: unknown) => (v == null ? null : String(v))

export function mapHall(r: any): Hall {
  return { id: r.id, branch_id: r.branch_id, name: String(r.name ?? ''), sort: num(r.sort), active: r.active !== false }
}

export function mapTable(r: any): DiningTable {
  return {
    id: r.id, branch_id: r.branch_id, hall_id: r.hall_id ?? null, name: String(r.name ?? ''), seats: numOrNull(r.seats),
    sort: num(r.sort), active: r.active !== false,
    shape: r.shape === 'round' || r.shape === 'square' || r.shape === 'long' ? r.shape : null,
    reserved_at: r.reserved_at ?? null, reserved_name: str(r.reserved_name),
  }
}

function mapLine(r: any): BillLine {
  return {
    id: r.id, order_id: r.order_id, product_id: r.product_id ?? null, product_name: String(r.product_name ?? ''),
    quantity: num(r.quantity), note: str(r.note), station: r.station ?? null, status: r.status, batch: numOrNull(r.batch),
    added_by_name: str(r.added_by_name), created_at: r.created_at,
    sent_at: r.sent_at ?? null, ready_at: r.ready_at ?? null, served_at: r.served_at ?? null,
  }
}

export function mapBill(r: any): Bill {
  return {
    id: r.id, branch_id: r.branch_id, table_id: r.table_id, table_name: String(r.table_name ?? ''), hall_name: str(r.hall_name),
    status: r.status, revision: num(r.revision), guests: numOrNull(r.guests), opened_at: r.opened_at,
    opened_by_name: str(r.opened_by_name), sent_batches: num(r.sent_batches), bill_printed_at: r.bill_printed_at ?? null,
    bill_total: numOrNull(r.bill_total), sale_id: r.sale_id ?? null,
    lines: Array.isArray(r.lines) ? r.lines.map(mapLine) : [],
    seq: num(r.seq), waiter_id: r.waiter_id ?? null, waiter_name: str(r.waiter_name), check_requested_at: r.check_requested_at ?? null,
    service_bp: num(r.service_bp), left_at: r.left_at ?? null, last_print: mapLastPrint(r.last_print),
  }
}

function mapLastPrint(r: any): LastPrint | null {
  if (!r || typeof r !== 'object' || !r.id) return null
  const state = r.state === 'sent' || r.state === 'failed' ? r.state : 'claimed'
  return {
    id: String(r.id), state, requestAt: String(r.request_at ?? ''), claimedAt: String(r.claimed_at ?? ''), stateAt: r.state_at ?? null,
    code: String(r.code ?? ''), total: num(r.total),
  }
}

export function mapBatch(r: any): Batch {
  const slips: SlipStation[] = Array.isArray(r.slips)
    ? r.slips.map((s: any) => ({
        station: s.station,
        lines: Array.isArray(s.lines) ? s.lines.map((l: any) => ({ qty: num(l.qty), name: String(l.name ?? ''), note: str(l.note) })) : [],
      }))
    : []
  return {
    id: r.id, order_id: r.order_id, kind: r.kind, n: num(r.n), slips, reason: str(r.reason),
    table_name: String(r.table_name ?? ''), hall_name: str(r.hall_name), created_by_name: str(r.created_by_name), created_at: r.created_at,
  }
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const REF = new RegExp(`^table:(${UUID}):([0-9]{1,9})$`)

/** The payment's client ref: the bill and the revision the till is paying (192's trigger reads both). */
export function tableClientRef(billId: string, revision: number): string {
  return `table:${billId}:${revision}`
}

export function parseTableRef(ref: string | null | undefined): { billId: string; revision: number } | null {
  const m = ref ? REF.exec(ref) : null
  return m ? { billId: m[1], revision: Number(m[2]) } : null
}

export const liveLines = (bill: Bill): BillLine[] => bill.lines.filter((l) => l.status !== 'void')

export const unsentCount = (bill: Bill): number => bill.lines.filter((l) => l.status === 'new').length

/** A box in the drawing's own units (an SVG viewBox of TABLE_DRAWING.w × TABLE_DRAWING.h). */
export type DrawBox = { x: number; y: number; w: number; h: number; r: number }

/** The drawing a tile scales to its width (the owner's choice „ბ", 2026-11-01: the table itself is the button). */
export const TABLE_DRAWING = { w: 230, h: 158 } as const

/**
 * The drawn table of a tile, seen from above: up to four seats a round table with a chair per seat, five to ten a long
 * table with the chairs along both sides (the odd one on top), more than ten drawn as ten (the tile says the number).
 * Seats not given (null or 0) is a round table with no chairs, `unknown` — never guessed as four. The shape is a hint
 * from the seats, not the furniture (a floor plan with real shapes is a later release).
 */
export function tableDrawing(seats: number | null): { table: DrawBox; chairs: DrawBox[]; round: boolean; unknown: boolean } {
  const cx = TABLE_DRAWING.w / 2, cy = TABLE_DRAWING.h / 2
  const r1 = (v: number) => Math.round(v * 10) / 10
  const box = (x: number, y: number, w: number, h: number, r: number): DrawBox => ({ x: r1(x), y: r1(y), w: r1(w), h: r1(h), r: r1(r) })
  const n = seats != null && seats > 0 ? Math.min(Math.floor(seats), 10) : 0
  if (n <= 4) {
    const d = n === 0 ? 92 : n <= 2 ? 84 : 92
    const table = box(cx - d / 2, cy - d / 2, d, d, d / 2)
    if (n === 0) return { table, chairs: [], round: true, unknown: true }
    const c = 22, ring = d / 2 + 16
    const angles = n === 1 ? [180] : n === 2 ? [180, 0] : n === 3 ? [270, 30, 150] : [270, 0, 90, 180]
    const chairs = angles.map((a) => {
      const q = (a * Math.PI) / 180
      return box(cx + ring * Math.cos(q) - c / 2, cy + ring * Math.sin(q) - c / 2, c, c, 7)
    })
    return { table, chairs, round: true, unknown: false }
  }
  const cw = 22, ch = 16, gap = 10, pad = 10, h = 62, space = 6
  const top = Math.ceil(n / 2), bottom = n - top
  const w = top * cw + (top + 1) * gap + pad
  const tx = cx - w / 2, ty = cy - h / 2
  const row = (count: number, y: number) => Array.from({ length: count }, (_, i) => {
    const step = (w - 2 * gap) / count
    return box(tx + gap + step * i + (step - cw) / 2, y, cw, ch, 5)
  })
  return {
    table: box(tx, ty, w, h, 14),
    chairs: [...row(top, ty - ch - space), ...row(bottom, ty + h + space)],
    round: false,
    unknown: false,
  }
}

/** The sent lines, batch by batch in order — what the kitchen was told and still stands. */
export function linesByBatch(bill: Bill): { batch: number; lines: BillLine[] }[] {
  const by = new Map<number, BillLine[]>()
  for (const l of bill.lines) {
    if (l.status !== 'sent' || l.batch == null) continue
    by.set(l.batch, [...(by.get(l.batch) ?? []), l])
  }
  return [...by.entries()].sort((a, b) => a[0] - b[0]).map(([batch, lines]) => ({ batch, lines }))
}

export function minutesOpen(openedAt: string, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(openedAt).getTime()) / 60_000))
}

const round3 = (q: number) => Math.round(q * 1000) / 1000

function sumsByProduct(rows: { product_id: string | null; quantity: number }[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rows) {
    const k = r.product_id ?? ''
    m.set(k, (m.get(k) ?? 0) + r.quantity)
  }
  for (const [k, v] of m) m.set(k, round3(v))
  return m
}

/** The trigger's rule: the sale's ROOT items equal the bill's live lines per product, to the gram; nothing else. */
export function coversBill(
  bill: Bill,
  items: { product_id: string | null; quantity: number; ingredient_of?: string | null }[],
): boolean {
  const live = liveLines(bill)
  if (live.length === 0) return false
  const want = sumsByProduct(live)
  const got = sumsByProduct(items.filter((i) => !i.ingredient_of))
  if (want.size !== got.size) return false
  for (const [k, v] of want) if (got.get(k) !== v) return false
  return true
}

/** The bill's live lines as the checkout cart: one line per product (notes are the kitchen's, not the receipt's). */
export function billCartLines(bill: Bill, products: readonly Product[]): CartItem[] {
  const byId = new Map(products.map((p) => [p.id, p]))
  const out = new Map<string, CartItem>()
  for (const l of liveLines(bill)) {
    const key = l.product_id ?? ''
    const had = out.get(key)
    if (had) { had.quantity = round3(had.quantity + l.quantity); continue }
    const p = l.product_id ? byId.get(l.product_id) : undefined
    out.set(key, {
      product_id: key,
      product_name: p?.name ?? l.product_name,
      barcode: p?.barcode ?? null,
      unit_price: p?.sale_price ?? 0,
      wholesale_price: p?.wholesale_price ?? null,
      quantity: round3(l.quantity),
      unit: (p?.unit ?? 'piece') as ProductUnit,
      photo_url: p?.photo_url ?? null,
    })
  }
  return [...out.values()]
}

/** The bill priced exactly as the till prices a cart (`priceCart`, retail mode): the tile, the panel and the client
 *  check read this one figure. The checkout re-prices with the customer chosen there. */
export function billPriced(bill: Bill, products: readonly Product[], promotions: readonly Promotion[]): {
  rows: { product_id: string; name: string; qty: number; price: number; amount: number }[]
  total: number
} {
  const lines = billCartLines(bill, products)
  const priced = priceCart(lines, promotions, { wholesale: false, group: null })
  return {
    rows: lines.map((l) => {
      const p = priced.get(l.product_id)
      return { product_id: l.product_id, name: l.product_name, qty: l.quantity, price: p?.unit_price ?? l.unit_price, amount: p?.amount ?? 0 }
    }),
    total: cartTotalOf(lines, priced),
  }
}

/** A reply or a refresh replaces the till's copy only when it is not older — by `seq` (1.67): ready and served move
 *  `seq` but not the money's `revision`, so a delayed reply at the same revision must not erase a newer „ready". */
export function applyBillReply(current: Bill | undefined, reply: Bill): Bill {
  return current && reply.seq < current.seq ? current : reply
}

// ── 1.67 (193) — the kitchen state, the waiter's bill, the kitchen board, the waiter's ready feed ──

export type KitchenState = 'new' | 'kitchen' | 'ready' | 'served' | 'void'

/** Where a dish is. A sent dish with no station is „to take" at once: no screen would ever show it. */
export function kitchenState(line: BillLine): KitchenState {
  if (line.status === 'void') return 'void'
  if (line.status === 'new') return 'new'
  if (line.served_at) return 'served'
  return line.ready_at || line.station == null ? 'ready' : 'kitchen'
}

const byCreated = (a: BillLine, b: BillLine) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id)

/** The waiter's bill in four groups, each oldest first; a void line is in none. */
export function billGroups(bill: Bill): { toTake: BillLine[]; inKitchen: BillLine[]; fresh: BillLine[]; served: BillLine[] } {
  const g = { toTake: [] as BillLine[], inKitchen: [] as BillLine[], fresh: [] as BillLine[], served: [] as BillLine[] }
  for (const l of [...bill.lines].sort(byCreated)) {
    const s = kitchenState(l)
    if (s === 'ready') g.toTake.push(l)
    else if (s === 'kitchen') g.inKitchen.push(l)
    else if (s === 'new') g.fresh.push(l)
    else if (s === 'served') g.served.push(l)
  }
  return g
}

export const readyCount = (bill: Bill): number => bill.lines.filter((l) => kitchenState(l) === 'ready').length

/** A till paying a bill with a line not yet sent sends it first, so a café that pays at once never pays for a dish
 *  the kitchen never got (Codex Q2). */
export const needsSendBeforePay = (bill: Bill): boolean => bill.lines.some((l) => l.status === 'new')

/** The waiter's check request is still waiting: asked, and no check printed since. */
export const checkAsked = (bill: Bill): boolean =>
  !!bill.check_requested_at && (!bill.bill_printed_at || bill.check_requested_at > bill.bill_printed_at)

/** Two database timestamps are the same instant (one may carry microseconds, the other milliseconds). */
export const sameInstant = (a: string, b: string): boolean => a === b || (Date.parse(a) === Date.parse(b) && !Number.isNaN(Date.parse(a)))

/** A printing claimed and not reported sent within this long is shown as failed (the paper may not have come out). */
export const PRINT_STUCK_MS = 60_000

/** What became of the check asked for (spec G17) — the waiter's words and the till's amber read this one rule. Tied to
 *  the request itself (`request_at`), never to the claim's clock: a „new" request is stamped by `clock_timestamp()`
 *  after its claim's transaction began (Codex final #3). A copy never answers a request (196 leaves copies out of
 *  `last_print`). */
export function printState(bill: Bill, now: Date): 'none' | 'printing' | 'printed' | 'failed' {
  const lp = bill.last_print
  const req = bill.check_requested_at
  if (!lp || !req || !sameInstant(lp.requestAt, req)) return 'none'
  if (lp.state === 'sent') return 'printed'
  if (lp.state === 'failed') return 'failed'
  return now.getTime() - Date.parse(lp.claimedAt) >= PRINT_STUCK_MS ? 'failed' : 'printing'
}

/** The till's amber for a check (spec G17): still asked, or its printing failed / never reported within a minute — the
 *  claim stamps `bill_printed_at`, so without the second half a failed paper vanished from the till (Codex final #4). */
export const checkWanted = (bill: Bill, now: Date): boolean => checkAsked(bill) || printState(bill, now) === 'failed'

const label = (table: unknown, hall: unknown) => (hall ? `${String(hall)} · ${String(table ?? '')}` : String(table ?? ''))

export type KitchenLine = {
  id: string; name: string; qty: number; note: string | null; station: Station | null
  readyAt: string | null; servedAt: string | null; voidedAt: string | null; voidReason: string | null
}
export type KitchenCard = {
  billId: string; batch: number; table: string; waiter: string | null; billStatus: Bill['status']
  sentAt: string; seq: number; lines: KitchenLine[]
}

export function mapKitchenCards(raw: unknown): KitchenCard[] {
  if (!Array.isArray(raw)) return []
  return raw.map((c: any) => ({
    billId: c.bill_id, batch: num(c.batch), table: label(c.table_name, c.hall_name), waiter: str(c.waiter_name),
    billStatus: c.bill_status, sentAt: c.sent_at, seq: num(c.seq),
    lines: Array.isArray(c.lines) ? c.lines.map((l: any) => ({
      id: l.id, name: String(l.product_name ?? ''), qty: num(l.quantity), note: str(l.note), station: l.station ?? null,
      readyAt: l.ready_at ?? null, servedAt: l.served_at ?? null, voidedAt: l.voided_at ?? null, voidReason: str(l.void_reason),
    })) : [],
  }))
}

/** A mistaken ready tap can be taken back while the dish is not served nor cancelled (Codex final #8). */
export const kitchenUndoable = (l: KitchenLine): boolean => !!l.readyAt && !l.servedAt && !l.voidedAt

/** The minutes the undo strip and a cancellation notice stay on the kitchen screen. */
export const KITCHEN_RECENT_MIN = 5
/** From this many minutes after the send a card's time turns amber. */
export const KITCHEN_LATE_MIN = 10

const ms = (iso: string) => new Date(iso).getTime()

/** Active = a card with a live dish not ready, oldest send first; recent = every dish ready or cancelled, the last of
 *  them within KITCHEN_RECENT_MIN, newest first; anything older has left the screen. */
export function kitchenBoard(cards: readonly KitchenCard[], now: Date): { active: KitchenCard[]; recent: KitchenCard[] } {
  const active: KitchenCard[] = []
  const recent: { card: KitchenCard; at: number }[] = []
  for (const c of cards) {
    if (c.lines.some((l) => !l.voidedAt && !l.readyAt)) { active.push(c); continue }
    const at = Math.max(...c.lines.map((l) => ms(l.voidedAt ?? l.readyAt ?? c.sentAt)))
    if (now.getTime() - at < KITCHEN_RECENT_MIN * 60_000) recent.push({ card: c, at })
  }
  active.sort((a, b) => ms(a.sentAt) - ms(b.sentAt) || a.billId.localeCompare(b.billId) || a.batch - b.batch)
  recent.sort((a, b) => b.at - a.at)
  return { active, recent: recent.map((r) => r.card) }
}

export function isLate(sentAt: string, now: Date): boolean {
  return now.getTime() - ms(sentAt) >= KITCHEN_LATE_MIN * 60_000
}

export type ReadyLine = { id: string; name: string; qty: number; note: string | null; station: Station | null; readyAt: string | null }
export type ReadyGroup = { billId: string; tableId: string; table: string; paid: boolean; seq: number; lines: ReadyLine[] }

export function mapReadyGroups(raw: unknown): ReadyGroup[] {
  if (!Array.isArray(raw)) return []
  return raw.map((g: any) => ({
    billId: g.bill_id, tableId: g.table_id, table: label(g.table_name, g.hall_name), paid: g.bill_status === 'paid',
    seq: num(g.seq),
    lines: Array.isArray(g.lines) ? g.lines.map((l: any) => ({
      id: l.id, name: String(l.product_name ?? ''), qty: num(l.quantity), note: str(l.note), station: l.station ?? null,
      readyAt: l.ready_at ?? null,
    })) : [],
  }))
}

export const readyTotal = (groups: readonly ReadyGroup[]): number => groups.reduce((n, g) => n + g.lines.length, 0)

/** Tiles in the owner's order: halls by sort, then each table by sort and by name, „2" before „10". */
export function nextTileOrder<T extends Pick<DiningTable, 'hall_id' | 'sort' | 'name'>>(
  tables: readonly T[],
  halls: readonly Pick<Hall, 'id' | 'sort'>[],
): T[] {
  const hallRank = new Map([...halls].sort((a, b) => a.sort - b.sort).map((h, i) => [h.id, i]))
  const rank = (t: T) => (t.hall_id != null && hallRank.has(t.hall_id) ? hallRank.get(t.hall_id)! : Number.MAX_SAFE_INTEGER)
  return [...tables].sort((a, b) =>
    rank(a) - rank(b) || a.sort - b.sort || a.name.localeCompare(b.name, 'ka', { numeric: true }))
}

export type CartOp = 'update' | 'remove' | 'clear' | 'hold' | 'resume' | 'add' | 'quote' | 'prepaidLines'

/** True when the cart may change: never while a table checkout is open — the payment is exactly the bill. */
export function lockedCartOp(_op: CartOp, checkout: { billId: string; revision: number } | null): boolean {
  return checkout == null
}

/** „ტერასა · 5" for a sale that paid a bill, from the bills the device knows; null for any other sale. */
export function tableLabelFor(
  sale: { client_ref?: string | null },
  bills: ReadonlyMap<string, { table_name: string; hall_name: string | null }>,
): string | null {
  const ref = parseTableRef(sale.client_ref)
  const b = ref ? bills.get(ref.billId) : undefined
  if (!b) return null
  return b.hall_name ? `${b.hall_name} · ${b.table_name}` : b.table_name
}

// ── 1.68 (196) — the table that stays taken, its status, the bill's money with the service ──

/** A paid table whose guests have not left yet (`tables_seated`) */
export type Seated = {
  tableId: string; billId: string; closedAt: string; waiterId: string | null; waiterName: string | null; guests: number | null
}

export function mapSeated(raw: unknown): Seated[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((r) => r && r.table_id && r.bill_id).map((r: any) => ({
    tableId: String(r.table_id), billId: String(r.bill_id), closedAt: String(r.closed_at ?? ''),
    waiterId: r.waiter_id ?? null, waiterName: str(r.waiter_name), guests: numOrNull(r.guests),
  }))
}

/** A table stays seated after its payment for at most 12 hours — a forgotten one never blocks the next day (G22) */
export const SEATED_HOURS = 12

export function seatedFor(seated: readonly Seated[] | null | undefined, tableId: string, now: Date): Seated | null {
  const row = seated?.find((s) => s.tableId === tableId)
  if (!row) return null
  // a payment newer than the screen's minute clock (or a clock a little behind the server's) is seated, never free —
  // the server's own read already drops what is older than 12 h (the 1.68 walk, W7)
  const age = now.getTime() - new Date(row.closedAt).getTime()
  return !Number.isNaN(age) && age < SEATED_HOURS * 3_600_000 ? row : null
}

/** 1.69 adds 'waiting' (a party seated, no bill yet) and 'reserved' (inside a reservation's window) — shown only where
 *  the table QR gives the tile a party (tileView's `qr`) */
export type TableStatus = 'free' | 'taken' | 'paid' | 'attention' | 'waiting' | 'reserved'

/** free · taken (an open bill) · attention (its check wanted, or a dish ready to carry) · paid (guests still seated) */
export function tableStatus(bill: Bill | undefined, seated: Seated | null, now: Date = new Date()): TableStatus {
  if (bill) return checkWanted(bill, now) || readyCount(bill) > 0 ? 'attention' : 'taken'
  return seated ? 'paid' : 'free'
}

/** The bill's money as every screen and the check show it: the dishes as the sale will store them, the service of
 *  the bill's rate (lib/service-charge — the trigger's rule), the total. The checkout re-prices with its customer. */
export function billMoney(bill: Bill, products: readonly Product[], promotions: readonly Promotion[], _serviceProductId: string | null): {
  rows: ReturnType<typeof billPriced>['rows']
  subtotal: number
  service: number
  total: number
} {
  const { rows } = billPriced(bill, products, promotions)
  const base = rows.reduce((t, r) => t + toTetri(r.amount), 0)
  const svc = serviceTetri(base, bill.service_bp)
  return { rows, subtotal: base / 100, service: svc / 100, total: (base + svc) / 100 }
}

/** Where a product's dishes go — the server's own rule (`private.table_station`): its category's station, else that
 *  category's parent's (one level). None = no screen shows it; the waiter carries it himself (water, a can). */
export function productStation(
  categories: readonly { id: string; parent_id: string | null; kitchen_station?: Station | null }[],
  categoryId: string | null | undefined,
): Station | null {
  if (!categoryId) return null
  const c = categories.find((x) => x.id === categoryId)
  if (!c) return null
  if (c.kitchen_station) return c.kitchen_station
  const parent = c.parent_id ? categories.find((x) => x.id === c.parent_id) : undefined
  return parent?.kitchen_station ?? null
}

/** What one table's tile says (spec §7) — the till's tables screen and the waiter's phone read the same: the status, the
 *  photo, the guests (the open bill's, else the seated payment's) and the seats, what needs attention (the check asked
 *  first, then dishes ready), and for an open bill its total, minutes open and waiter; for a paid one, when it was paid. */
export type TileView = {
  status: TableStatus
  photo: string
  guests: number | null
  seats: number | null
  attention: 'check' | 'ready' | null
  readyCount: number
  open: { total: number; minutes: number; waiter: string | null } | null
  paidAt: string | null
  /** 1.69 — the party with no bill yet (minutes since it sat; a scan's QR mark), or the reservation mark */
  party: TileParty | null
  /** 1.69 — the table's open guest requests: the oldest's kind and age, amber when late, how many */
  request: TileRequest | null
}

export type TileParty =
  | { kind: 'waiting'; minutes: number; qr: boolean }
  | { kind: 'reserved' | 'reservedLater'; at: string; name: string | null }
export type TileRequest = { kind: RequestKind; minutes: number; late: boolean; count: number }

export function tileView(table: DiningTable, bill: Bill | undefined, seated: Seated | null, total: number, now: Date,
  qr?: { visit: Visit | null; requests: readonly GuestRequest[] }): TileView {
  const ready = bill ? readyCount(bill) : 0
  let status = tableStatus(bill, seated, now)
  let party: TileParty | null = null
  let request: TileRequest | null = null
  if (qr) {
    // 1.69 (spec §5.2) — the party at the table, the reservation and the guest's requests: one rule, `tableState`
    const state = tableState(table, bill, qr.visit, seated, now)
    if (state === 'waiting' && qr.visit) {
      const minutes = minutesOpen(qr.visit.startedAt, now)
      party = { kind: 'waiting', minutes, qr: qr.visit.startedVia === 'qr' }
      status = minutes >= TABLE_WAIT_LATE_MIN ? 'attention' : 'waiting'
    } else if ((state === 'reserved' || state === 'reservedLater') && table.reserved_at) {
      party = { kind: state, at: table.reserved_at, name: table.reserved_name }
      if (state === 'reserved') status = 'reserved'
    }
    const open = qr.requests.filter((r) => r.tableId === table.id && (r.status === 'waiting' || r.status === 'taken'))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    if (open.length > 0) {
      const late = isLateRequest(open[0], now)
      request = { kind: open[0].kind, minutes: minutesOpen(open[0].createdAt, now), late, count: open.length }
      if (late) status = 'attention'
    }
  }
  return {
    status,
    photo: tablePhotoSrc(table),
    guests: bill?.guests ?? seated?.guests ?? null,
    seats: table.seats,
    attention: bill ? (checkWanted(bill, now) ? 'check' : ready > 0 ? 'ready' : null) : null,
    readyCount: ready,
    open: bill ? { total, minutes: minutesOpen(bill.opened_at, now), waiter: bill.waiter_name } : null,
    paidAt: !bill && seated ? seated.closedAt : null,
    party,
    request,
  }
}

/** From this many minutes a seated party with no bill yet is shown amber — waiting for us (spec §5.2) */
export const TABLE_WAIT_LATE_MIN = 5

// ── 1.69 (197) — the party at a table (a visit), the six states, availability ──

/** A party at a table (`tables_visits`): from the scan, „სტუმარი დაჯდა" or the first bill, until staff free the table */
export type Visit = {
  id: string; tableId: string; startedAt: string; startedVia: 'qr' | 'staff' | 'claim' | 'bill'
  waiterId: string | null; waiterName: string | null
}

const VIA = new Set(['qr', 'staff', 'claim', 'bill'])

export function mapVisits(raw: unknown): Visit[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((r) => r && r.visit_id && r.table_id).map((r: any) => ({
    id: String(r.visit_id), tableId: String(r.table_id), startedAt: String(r.started_at ?? ''),
    startedVia: VIA.has(r.started_via) ? r.started_via : 'staff', waiterId: r.waiter_id ?? null, waiterName: str(r.waiter_name),
  }))
}

/** The server's numbers (197): a visit with no open bill is stale after 12 hours; a reservation holds its table from
 *  60 minutes before to 60 minutes after its time. */
export const VISIT_STALE_HOURS = 12
export const RESERVE_WINDOW_MIN = 60

export type TableState = 'free' | 'reservedLater' | 'reserved' | 'waiting' | 'serving' | 'paid'

/** The one rule of a table's state (spec §5.2; the server's `private.table_available`): an open bill → serving; paid
 *  with the guests still seated (G22) → paid; a party seated with no bill → waiting; inside a reservation's window →
 *  reserved; a reservation further away → free, reserved later; else free. `bill` is the table's OPEN bill. */
export function tableState(t: DiningTable, bill: Bill | undefined, visit: Visit | null, seated: Seated | null, now: Date): TableState {
  if (bill) return 'serving'
  if (seated) return 'paid'
  if (visit && visit.tableId === t.id) {
    const age = now.getTime() - Date.parse(visit.startedAt)
    // a start newer than the screen's clock is live, never stale (as seatedFor)
    if (Number.isNaN(age) || age < VISIT_STALE_HOURS * 3_600_000) return 'waiting'
  }
  const at = t.reserved_at ? Date.parse(t.reserved_at) : NaN
  if (!Number.isNaN(at)) {
    const away = at - now.getTime()
    if (Math.abs(away) <= RESERVE_WINDOW_MIN * 60_000) return 'reserved'
    if (away > 0) return 'reservedLater'
  }
  return 'free'
}

/** May receive a moved bill or a new party: free, or free with a reservation later (the server refuses the rest) */
export const tableAvailable = (state: TableState): boolean => state === 'free' || state === 'reservedLater'

/** 1.69 (edge 23) — with „free the table on payment" on, the payment ends the party's visit and closes its guests' orders
 *  not yet confirmed: how many the checkout must say first. Off, or no bill: none. */
export function freeOnPayWarning(bill: Bill | undefined, requests: readonly GuestRequest[], freeOnPay: boolean): number {
  if (!bill || !freeOnPay) return 0
  return requests.filter((r) => r.tableId === bill.table_id && r.kind === 'order' && (r.status === 'waiting' || r.status === 'taken')).length
}

/** 1.69 (edge 14) — where a bill may move: an active table of its branch, not its own. With the table QR the server's
 *  own rule (`private.table_available`, the move trigger refuses the rest): free or reserved later only. Without it,
 *  1.68's rule: any table without an open bill. */
export function moveTargets(
  tables: readonly DiningTable[], bills: Readonly<Record<string, Bill>> | null, seated: readonly Seated[] | null,
  visits: readonly Visit[] | null, bill: Bill, now: Date, qr: boolean,
): DiningTable[] {
  const billOn = new Map(Object.values(bills ?? {}).map((b) => [b.table_id, b]))
  return tables.filter((x) => {
    if (x.branch_id !== bill.branch_id || !x.active || x.id === bill.table_id || billOn.has(x.id)) return false
    if (!qr) return true
    const v = visits?.find((y) => y.tableId === x.id) ?? null
    return tableAvailable(tableState(x, undefined, v, seatedFor(seated, x.id, now), now))
  })
}
